import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { logActivity } from "@/lib/activityLogger";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  providerToken: string | null;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  providerToken: null,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [providerToken, setProviderToken] = useState<string | null>(null);

  const lastLoggedEvent = useRef<string | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (session?.provider_token) {
        setProviderToken(session.provider_token);
      }
      setLoading(false);

      // Log auth events (deduplicate with ref)
      const eventKey = `${event}-${session?.user?.id ?? "none"}`;
      if (eventKey !== lastLoggedEvent.current) {
        lastLoggedEvent.current = eventKey;
        if (event === "SIGNED_IN" && session?.user) {
          logActivity({ action: "User logged in", details: session.user.email ?? "Unknown" });
        } else if (event === "USER_UPDATED" && session?.user) {
          logActivity({ action: "User updated profile via auth", details: session.user.email ?? "Unknown" });
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.provider_token) {
        setProviderToken(session.provider_token);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    setProviderToken(null);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, providerToken, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
